import React from "react";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import { DAY_NAMES, dayState, monthGrid, parse } from "./rules";

const STYLE = {
  closed: "bg-red-50 border-red-200 text-red-800",
  extra: "bg-amber-50 border-amber-300 text-amber-900",
  usual: "bg-emerald-50 border-emerald-200 text-emerald-900",
  off: "bg-stone-50 border-stone-200 text-ink-muted",
};
const LABEL = { closed: "Not available", extra: "Extra hours", usual: "Usual hours", off: "Not working" };

export const Legend = () => (
  <div className="flex flex-wrap gap-3 text-xs" data-testid="availability-legend">
    {Object.keys(STYLE).map((k) => (
      <span key={k} className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-sm border ${STYLE[k]}`}>{LABEL[k]}</span>
    ))}
    <span className="inline-flex items-center gap-1.5 px-2 py-1 text-ink-muted"><span className="w-2 h-2 rounded-full bg-bronze-dark" /> booked</span>
  </div>
);

const DayCell = ({ day, rule, month, today, minDay, free, booked, selected, onPick }) => {
  const st = dayState(rule, day);
  const outside = day.slice(0, 7) !== month;
  const past = day < today;
  const notice = !past && day <= minDay;
  const empty = st.kind === "usual" && free === 0 && !notice && !past;
  const kind = empty ? "off" : st.kind;
  return (
    <button type="button" onClick={() => onPick(day)} data-testid={`cal-day-${day}`} data-state={kind}
      className={`min-h-[56px] sm:min-h-[92px] p-1 sm:p-2 text-left overflow-hidden rounded-sm border transition-shadow hover:shadow-md ${STYLE[kind]} ${outside ? "opacity-40" : ""} ${past ? "opacity-50" : ""} ${selected ? "ring-2 ring-bronze-dark" : ""}`}>
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">{parse(day).getDate()}</span>
        {booked > 0 && <span data-testid={`cal-booked-${day}`} className="text-[10px] px-1.5 rounded-full bg-bronze-dark text-white">{booked}<span className="hidden sm:inline"> booked</span></span>}
      </div>
      <div className="hidden sm:block mt-1 text-[11px] leading-tight">{empty ? "No clinic hours" : st.kind === "extra" ? "Extra hours" : LABEL[st.kind]}</div>
      {(st.times || st.hours) && <div className="hidden sm:block text-[11px] font-medium">{(st.times || st.hours).join(", ")}</div>}
      {notice && <div className="hidden sm:block text-[10px] text-ink-muted mt-1">Inside notice period</div>}
      {free !== undefined && !past && !notice && kind !== "closed" && kind !== "off" && (
        <div data-testid={`cal-free-${day}`} className="hidden sm:block text-[10px] text-ink-muted mt-1">{free ? `${free} open to book now` : st.kind === "usual" ? "None free" : "No free times"}</div>
      )}
    </button>
  );
};

export const MonthCalendar = ({ rule, month, setMonth, today, minDay, freeByDay, bookedByDay, selected, onPick }) => {
  const label = parse(`${month}-01`).toLocaleDateString("en-GB", { month: "long", year: "numeric" });
  const shift = (n) => { const d = parse(`${month}-01`); d.setMonth(d.getMonth() + n); setMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`); };
  return (
    <div data-testid="availability-calendar">
      <div className="flex items-center justify-between mb-3">
        <button type="button" onClick={() => shift(-1)} data-testid="cal-prev-month" aria-label="Previous month" className="p-2 rounded-full hover:bg-cream-100"><CaretLeft size={18} /></button>
        <div className="font-serif text-2xl" data-testid="cal-month-label">{label}</div>
        <button type="button" onClick={() => shift(1)} data-testid="cal-next-month" aria-label="Next month" className="p-2 rounded-full hover:bg-cream-100"><CaretRight size={18} /></button>
      </div>
      <div className="grid grid-cols-7 gap-1.5 text-xs uppercase tracking-[0.12em] text-ink-muted mb-1.5">
        {DAY_NAMES.map((d) => <div key={d} className="px-1">{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1 sm:gap-1.5">
        {monthGrid(`${month}-01`).map((d) => (
          <DayCell key={d} day={d} rule={rule} month={month} today={today} minDay={minDay} free={freeByDay?.[d]}
            booked={bookedByDay[d] || 0} selected={selected === d} onPick={onPick} />
        ))}
      </div>
    </div>
  );
};
